// Keyboard movement through menus and groups of controls, shared by every
// menu, toolbar, tab row and swatch grid (see the keyboard rules K1–K11 in
// docs/keyboard-navigation.md).
//
// v-menu goes on an open menu's element. While it is the topmost open menu it
// owns Escape (close), the arrows, Home/End, type-ahead and Tab, and when it
// closes it hands focus back to wherever focus was when it opened.
//
// v-roving goes on a group (a bar's keys, a row of tabs, a segmented control,
// a swatch grid). The group is a single Tab stop; the arrows move inside it.

import type { Directive } from 'vue'

export type NavOrientation = 'vertical' | 'horizontal' | 'grid'

/** What counts as an item in a menu or group, unless the group says otherwise. */
const ITEM_SELECTOR = [
  'button',
  '[role="menuitem"]',
  '[role="menuitemradio"]',
  '[role="menuitemcheckbox"]',
  '[role="option"]',
  '[role="tab"]',
  '[role="radio"]',
  'a[href]',
  'input:not([type="hidden"])',
  'select',
  'textarea',
].join(', ')

// ── Which input the writer used last ────────────────────────────────────────

let lastInput: 'keyboard' | 'pointer' = 'pointer'
let inputWatched = false

function watchInput() {
  if (inputWatched || typeof window === 'undefined') return
  inputWatched = true
  window.addEventListener('keydown', () => { lastInput = 'keyboard' }, true)
  window.addEventListener('pointerdown', () => { lastInput = 'pointer' }, true)
}

// ── Items ───────────────────────────────────────────────────────────────────

function isDisabled(el: HTMLElement): boolean {
  return (el as HTMLButtonElement).disabled === true
    || el.getAttribute('aria-disabled') === 'true'
    || el.closest('[hidden], [inert], [aria-hidden="true"]') !== null
}

/** The items of a container in DOM order: enabled, shown, and not inside a nested menu or group. */
export function navItems(container: HTMLElement, selector = ITEM_SELECTOR): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(selector)).filter((el) => {
    if (isDisabled(el) || el.closest('[data-nav-skip]')) return false
    // An item inside a nested group belongs to that group.
    const owner = el.parentElement?.closest('[data-nav-group]')
    return !owner || owner === container || !container.contains(owner)
  })
}

/** Text fields keep their own arrows, Home/End and letters. */
export function isTextField(el: Element | null): boolean {
  if (!el) return false
  if (el instanceof HTMLTextAreaElement) return true
  if (el instanceof HTMLSelectElement) return true
  if (el instanceof HTMLInputElement) {
    return !['button', 'checkbox', 'radio', 'submit', 'reset', 'color', 'range', 'file', 'image'].includes(el.type)
  }
  return (el as HTMLElement).isContentEditable === true
}

/** The item a menu or group opens on: the one marked current, or the first. */
function currentItem(items: HTMLElement[]): HTMLElement | undefined {
  return items.find((el) => el.matches(
    '[aria-checked="true"], [aria-selected="true"], [aria-current]:not([aria-current="false"]), [aria-pressed="true"], .active',
  )) ?? items.find((el) => el.getAttribute('tabindex') === '0') ?? items[0]
}

export function focusItem(el: HTMLElement | undefined) {
  if (!el) return
  el.focus({ preventScroll: true })
  el.scrollIntoView?.({ block: 'nearest' })
}

// ── Movement ────────────────────────────────────────────────────────────────

const NEXT: Record<Exclude<NavOrientation, 'grid'>, string> = { vertical: 'ArrowDown', horizontal: 'ArrowRight' }
const PREV: Record<Exclude<NavOrientation, 'grid'>, string> = { vertical: 'ArrowUp', horizontal: 'ArrowLeft' }

/** The item to move to in a grid: the nearest in the row above or below, by position. */
function gridTarget(items: HTMLElement[], index: number, key: string): HTMLElement | undefined {
  const from = items[index].getBoundingClientRect()
  const rects = items.map((el) => el.getBoundingClientRect())
  // No layout (a hidden menu, or tests): treat the grid as one row.
  if (rects.every((r) => r.width === 0 && r.height === 0)) {
    if (key === 'ArrowRight' || key === 'ArrowDown') return items[index + 1]
    return items[index - 1]
  }
  const cx = from.left + from.width / 2
  const cy = from.top + from.height / 2
  if (key === 'ArrowLeft' || key === 'ArrowRight') {
    const step = key === 'ArrowRight' ? 1 : -1
    return items[index + step]
  }
  const below = key === 'ArrowDown'
  let best: HTMLElement | undefined
  let bestRow = Infinity
  let bestX = Infinity
  rects.forEach((r, i) => {
    if (i === index) return
    const y = r.top + r.height / 2
    const rowDistance = below ? y - cy : cy - y
    if (rowDistance < r.height / 2) return
    const x = Math.abs(r.left + r.width / 2 - cx)
    // Nearest row first, then the nearest item across it.
    if (rowDistance < bestRow - 1 || (Math.abs(rowDistance - bestRow) <= 1 && x < bestX)) {
      best = items[i]
      bestRow = rowDistance
      bestX = x
    }
  })
  return best
}

/**
 * Where a key moves focus among items, or null when the key is not a
 * movement key for this orientation. Wraps at the ends unless told not to.
 */
export function moveTarget(
  items: HTMLElement[],
  active: Element | null,
  key: string,
  orientation: NavOrientation,
  wrap = true,
): HTMLElement | null {
  if (!items.length) return null
  const index = items.indexOf(active as HTMLElement)
  if (key === 'Home') return items[0]
  if (key === 'End') return items[items.length - 1]
  if (orientation === 'grid') {
    if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(key)) return null
    if (index < 0) return key === 'ArrowUp' || key === 'ArrowLeft' ? items[items.length - 1] : items[0]
    return gridTarget(items, index, key) ?? items[index]
  }
  const forward = key === NEXT[orientation]
  if (!forward && key !== PREV[orientation]) return null
  if (index < 0) return forward ? items[0] : items[items.length - 1]
  const next = index + (forward ? 1 : -1)
  if (next < 0) return wrap ? items[items.length - 1] : items[0]
  if (next >= items.length) return wrap ? items[0] : items[items.length - 1]
  return items[next]
}

/** An item's name for type-ahead: its label, without shortcut hints or leading marks like ¶ or ❝. */
function typeaheadLabel(item: HTMLElement): string {
  let text = item.getAttribute('aria-label') ?? ''
  if (!text) {
    const copy = item.cloneNode(true) as HTMLElement
    copy.querySelectorAll('kbd, .tbm-kbd, .item-shortcut, [aria-hidden="true"]').forEach((el) => el.remove())
    text = copy.textContent ?? ''
  }
  return text.replace(/^[^\p{L}\p{N}]+/u, '').toLocaleLowerCase()
}

/** The next item whose name starts with a letter, after the active one, wrapping. */
export function typeaheadTarget(items: HTMLElement[], active: Element | null, char: string): HTMLElement | null {
  const wanted = char.toLocaleLowerCase()
  const start = items.indexOf(active as HTMLElement)
  for (let step = 1; step <= items.length; step++) {
    const item = items[(start + step + items.length) % items.length]
    if (typeaheadLabel(item).startsWith(wanted)) return item
  }
  return null
}

function isPrintable(e: KeyboardEvent): boolean {
  return e.key.length === 1 && e.key !== ' ' && !e.ctrlKey && !e.metaKey && !e.altKey
}

// ── v-menu ──────────────────────────────────────────────────────────────────

export interface MenuNavOptions {
  /** Closes the menu (Escape, Tab, or ← out of a submenu). */
  close: () => void
  /** How the arrows move: down a list (the default), or around a grid. */
  orientation?: NavOrientation
  /** What counts as an item, when not the usual buttons and menu items. */
  items?: string
  /** A submenu: ← closes it and goes back to the item that opened it. */
  submenu?: boolean
  /**
   * Tab moves between the menu's parts (a swatch grid, its Reset link and
   * code box) and stays inside, instead of closing the menu.
   */
  keepTab?: boolean
  /**
   * A list with a search box (K3): the box gets focus when the menu opens and
   * keeps it, while ↑/↓ and PgUp/PgDn move a highlight and Enter picks it.
   */
  search?: boolean
  /**
   * Where focus goes when the menu closes without anything else taking it.
   * By default, back to what had focus when the menu opened.
   */
  returnFocus?: (opener: HTMLElement | null) => void
}

interface OpenMenu {
  el: HTMLElement
  options: MenuNavOptions
  opener: HTMLElement | null
  /** The highlighted item in a search list. */
  highlight: HTMLElement | null
  /** Opened with a click rather than a key. */
  byPointer: boolean
}

let pageFocus: (() => void) | null = null

/** How to put the keyboard back in the page; the app shell provides it. */
export function setPageFocusHandler(handler: (() => void) | null) {
  pageFocus = handler
}

export function focusPage() {
  pageFocus?.()
}

const HIGHLIGHT_CLASS = 'kb-highlight'
const PAGE_STEP = 8

function setHighlight(menu: OpenMenu, item: HTMLElement | null) {
  if (menu.highlight === item) return
  menu.highlight?.classList.remove(HIGHLIGHT_CLASS)
  menu.highlight?.removeAttribute('aria-selected')
  menu.highlight = item
  if (!item) return
  item.classList.add(HIGHLIGHT_CLASS)
  item.setAttribute('aria-selected', 'true')
  item.scrollIntoView?.({ block: 'nearest' })
}

function searchItems(menu: OpenMenu): HTMLElement[] {
  return navItems(menu.el, menu.options.items).filter((item) => !isTextField(item))
}

/** Arrows, paging and Enter in a search list's box. Returns whether it used the key. */
function handleSearchKey(menu: OpenMenu, e: KeyboardEvent): boolean {
  const items = searchItems(menu)
  if (!items.length) return false
  const index = menu.highlight ? items.indexOf(menu.highlight) : -1
  let next: number | null = null
  if (e.key === 'ArrowDown') next = index < 0 ? 0 : (index + 1) % items.length
  else if (e.key === 'ArrowUp') next = index < 0 ? items.length - 1 : (index - 1 + items.length) % items.length
  else if (e.key === 'PageDown') next = Math.min(items.length - 1, Math.max(0, index) + PAGE_STEP)
  else if (e.key === 'PageUp') next = Math.max(0, index - PAGE_STEP)
  else if (e.key === 'Enter') {
    const pick = index >= 0 ? items[index] : items[0]
    pick.click()
    return true
  }
  if (next === null) return false
  setHighlight(menu, items[next])
  return true
}

/** Typing narrows the list: the highlight goes back to the first match. */
function handleSearchInput(this: HTMLElement) {
  const menu = (this as any).__menuNav as OpenMenu | undefined
  if (!menu) return
  // After the list re-renders for the new query.
  setTimeout(() => {
    if (!menu.el.isConnected) return
    const field = document.activeElement as HTMLInputElement | null
    const query = field && isTextField(field) ? field.value.trim() : ''
    setHighlight(menu, query ? searchItems(menu)[0] ?? null : null)
  }, 0)
}

const menuStack: OpenMenu[] = []
let menuKeysInstalled = false
// A menu item that opens another menu (List Options… in the right-click
// menu) closes its own menu first; the new one inherits where to go back to.
let closingMenu: OpenMenu | null = null

function topMenu(): OpenMenu | undefined {
  return menuStack[menuStack.length - 1]
}

function handleMenuKeydown(e: KeyboardEvent) {
  const menu = topMenu()
  if (!menu || e.isComposing || !menu.el.isConnected) return
  const { el, options } = menu
  const active = document.activeElement
  const inside = !!active && el.contains(active)
  const inField = inside && isTextField(active)
  // A field that edits one of the menu's items in place (renaming it) keeps
  // every key, Enter and Esc included, for itself.
  if (inside && (active as HTMLElement).closest('[data-own-keys]')) return

  if (e.key === 'Escape') {
    e.preventDefault()
    e.stopImmediatePropagation()
    options.close()
    return
  }

  if (e.ctrlKey || e.metaKey || e.altKey) return
  const items = navItems(el, options.items)
  const orientation = options.orientation ?? 'vertical'

  if (e.key === 'Tab') {
    if (!inside) return
    if (options.keepTab) {
      const stops = Array.from(el.querySelectorAll<HTMLElement>('button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]'))
        .filter((item) => !isDisabled(item) && item.tabIndex >= 0)
      if (!stops.length) return
      e.preventDefault()
      const index = stops.indexOf(active as HTMLElement)
      const next = e.shiftKey ? (index <= 0 ? stops.length - 1 : index - 1) : (index + 1) % stops.length
      focusItem(stops[next])
      return
    }
    // Tab closes the menu and carries on from the key that opened it. The
    // page itself takes Tab for lists, so a menu opened from the page just closes.
    const opener = menu.opener
    options.close()
    if (opener?.isConnected && !opener.isContentEditable && opener !== document.body) opener.focus({ preventScroll: true })
    else e.preventDefault()
    return
  }

  if (options.submenu && e.key === 'ArrowLeft' && inside && !inField) {
    e.preventDefault()
    e.stopPropagation()
    options.close()
    return
  }

  if (orientation !== 'grid' && e.key === 'ArrowRight' && inside && !inField) {
    const opensSubmenu = active instanceof HTMLElement && active.getAttribute('aria-haspopup') === 'menu'
    if (opensSubmenu) {
      e.preventDefault()
      e.stopPropagation()
      active.click()
    }
    return
  }

  // A menu opened with the pointer leaves focus where it was (usually the
  // page); the first arrow press takes the keyboard into the menu.
  if (!inside) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End', ...(orientation === 'grid' ? ['ArrowLeft', 'ArrowRight'] : [])].includes(e.key)) return
    e.preventDefault()
    e.stopPropagation()
    const start = e.key === 'ArrowUp' || e.key === 'End' ? items[items.length - 1] : currentItem(items)
    focusItem(start)
    return
  }

  // A group inside the menu (a swatch grid) moves its own focus.
  const group = active instanceof HTMLElement ? active.closest('[data-nav-group]') : null
  if (group && group !== el && el.contains(group)) return

  if (inField && options.search) {
    if (handleSearchKey(menu, e)) {
      e.preventDefault()
      e.stopPropagation()
    }
    return
  }

  if (inField) {
    // A field in a menu keeps its caret keys; ↑/↓ leave it for the items.
    if (orientation === 'grid' || (e.key !== 'ArrowDown' && e.key !== 'ArrowUp')) return
  }

  const target = moveTarget(items, active, e.key, orientation)
  if (target) {
    e.preventDefault()
    e.stopPropagation()
    focusItem(target)
    return
  }

  if (!inField && (e.key === 'Enter' || e.key === ' ') && active instanceof HTMLElement && !(active instanceof HTMLButtonElement) && !(active instanceof HTMLAnchorElement)) {
    // Buttons press themselves on Enter and Space; other items need a click.
    e.preventDefault()
    e.stopPropagation()
    active.click()
    return
  }

  if (!inField && isPrintable(e)) {
    const match = typeaheadTarget(items, active, e.key)
    if (match) {
      e.preventDefault()
      e.stopPropagation()
      focusItem(match)
    }
  }
}

function installMenuKeys() {
  if (menuKeysInstalled || typeof window === 'undefined') return
  menuKeysInstalled = true
  // Capture, so the open menu answers before the page or a dialog beneath it.
  window.addEventListener('keydown', handleMenuKeydown, true)
}

function restoreFocus(menu: OpenMenu) {
  const active = document.activeElement
  // Something else took focus as the menu closed (a chosen item focusing the
  // page, a dialog opening): leave it there.
  if (active && active !== document.body && !menu.el.contains(active) && active.isConnected) return
  if (menu.options.returnFocus) {
    menu.options.returnFocus(menu.opener)
    return
  }
  // A toolbar menu opened with a click goes back to the page; the bar hides
  // again once the pointer leaves, so leaving focus on its key would strand it.
  if (menu.byPointer && menu.opener?.closest('.bar-keys') && pageFocus) {
    pageFocus()
    return
  }
  focusOpener(menu.opener)
}

/** Puts focus back on what opened a menu. The page gets its own cursor back, not just DOM focus. */
export function focusOpener(opener: HTMLElement | null) {
  if (!opener?.isConnected || opener === document.body) return
  const page = opener.isContentEditable ? opener.closest<HTMLElement>('.ProseMirror') : null
  const editor = (page as any)?.editor
  if (editor && !editor.isDestroyed) editor.commands.focus(null, { scrollIntoView: false })
  else opener.focus({ preventScroll: true })
}

export const vMenu: Directive<HTMLElement, MenuNavOptions> = {
  mounted(el, binding) {
    watchInput()
    installMenuKeys()
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const opener = closingMenu && active && closingMenu.el.contains(active) ? closingMenu.opener : active
    const menu: OpenMenu = { el, options: binding.value, opener, highlight: null, byPointer: lastInput === 'pointer' }
    menuStack.push(menu)
    ;(el as any).__menuNav = menu
    if (el.getAttribute('role') === null) el.setAttribute('role', 'menu')
    // Its items are its own, not part of the bar or group it opened from.
    el.setAttribute('data-nav-group', '')
    if (binding.value.search) {
      el.addEventListener('input', handleSearchInput)
      queueMicrotask(() => {
        if (!el.isConnected) return
        const field = Array.from(el.querySelectorAll<HTMLElement>('input, textarea')).find(isTextField)
        field?.focus({ preventScroll: true })
        const current = searchItems(menu).find((item) => item.matches('.active, .selected, [aria-checked="true"]'))
        setHighlight(menu, current ?? null)
      })
      return
    }
    // Opened from the keyboard: start on the current choice. A component
    // that focused a field of its own (a search box) keeps it.
    if (lastInput === 'keyboard') {
      queueMicrotask(() => {
        if (!el.isConnected || el.contains(document.activeElement)) return
        focusItem(currentItem(navItems(el, binding.value.items)))
      })
    }
  },
  updated(el, binding) {
    const menu = (el as any).__menuNav as OpenMenu | undefined
    if (menu) menu.options = binding.value
  },
  beforeUnmount(el) {
    const menu = (el as any).__menuNav as OpenMenu | undefined
    if (!menu) return
    el.removeEventListener('input', handleSearchInput)
    const index = menuStack.indexOf(menu)
    if (index >= 0) menuStack.splice(index, 1)
    // After the menu leaves the page, so focus inside it has already fallen away.
    closingMenu = menu
    queueMicrotask(() => {
      if (closingMenu === menu) closingMenu = null
      restoreFocus(menu)
    })
  },
}

// ── v-roving ────────────────────────────────────────────────────────────────

export interface RovingOptions {
  orientation?: NavOrientation
  /** What counts as an item, when not the usual buttons, tabs and radios. */
  items?: string
  /** Wrap from the last item to the first. On by default. */
  wrap?: boolean
  /** Moving onto an item also chooses it (tabs, segmented controls). */
  selectOnFocus?: boolean
  /**
   * Where the Tab stop starts: on the current item (a tab row, a segmented
   * control) or the first (a bar, whose lit keys are formatting, not a choice).
   */
  initial?: 'current' | 'first'
  /** Leave text fields and sliders out of the arrows; they keep their own keys and their Tab stop. */
  skipFields?: boolean
}

function isSlider(el: Element | null): boolean {
  return el instanceof HTMLInputElement && el.type === 'range'
}

function rovingItems(el: HTMLElement, options: RovingOptions): HTMLElement[] {
  const items = navItems(el, options.items)
  return options.skipFields ? items.filter((item) => !isTextField(item) && !isSlider(item)) : items
}

function rovingOptions(value: RovingOptions | NavOrientation | undefined): RovingOptions {
  if (typeof value === 'string') return { orientation: value }
  return value ?? {}
}

/** Leaves one item in the Tab order: the focused one, else the last one used, else the starting one. */
function updateTabStops(el: HTMLElement, options: RovingOptions) {
  const items = rovingItems(el, options)
  if (!items.length) return
  const active = document.activeElement as HTMLElement | null
  const previous = (el as any).__rovingStop as HTMLElement | undefined
  const stop = active && items.includes(active)
    ? active
    : previous && items.includes(previous)
      ? previous
      : options.initial === 'first' ? items[0] : currentItem(items)
  ;(el as any).__rovingStop = stop
  for (const item of items) item.tabIndex = item === stop ? 0 : -1
}

function handleRovingKeydown(this: HTMLElement, e: KeyboardEvent) {
  if (e.defaultPrevented || e.isComposing || e.ctrlKey || e.metaKey || e.altKey) return
  const options = ((this as any).__roving as RovingOptions) ?? {}
  const active = document.activeElement
  if (isTextField(active) || isSlider(active)) return
  const items = rovingItems(this, options)
  if (!items.includes(active as HTMLElement)) return
  const orientation = options.orientation ?? 'horizontal'
  // The arrow across the group opens a key's menu: ↓ on a bar along the
  // top or bottom, ← on the bar down the right side.
  const crossKey = orientation === 'horizontal' ? 'ArrowDown' : orientation === 'vertical' ? 'ArrowLeft' : null
  if (e.key === crossKey && active instanceof HTMLElement && active.getAttribute('aria-haspopup')) {
    e.preventDefault()
    active.click()
    return
  }
  const target = moveTarget(items, active, e.key, orientation, options.wrap ?? true)
  if (!target || target === active) return
  e.preventDefault()
  focusItem(target)
  updateTabStops(this, options)
  if (options.selectOnFocus) target.click()
}

function handleRovingFocusin(this: HTMLElement) {
  updateTabStops(this, ((this as any).__roving as RovingOptions) ?? {})
}

/**
 * Ctrl+Tab / Ctrl+Shift+Tab switch tabs from anywhere inside a panel (K6):
 * the nearest tab row around the focus. Not from the page, whose
 * surroundings (Editor Mode's tabs) aren't what the writer is in.
 */
function handleTabSwitch(e: KeyboardEvent) {
  if (e.key !== 'Tab' || !e.ctrlKey || e.altKey || e.metaKey || e.defaultPrevented) return
  const active = document.activeElement as HTMLElement | null
  if (!active || active === document.body || active.closest('.ProseMirror')) return
  let tablist: HTMLElement | null = null
  for (let node: HTMLElement | null = active; node && node !== document.body; node = node.parentElement) {
    tablist = node.matches('[role="tablist"]') ? node : node.querySelector<HTMLElement>('[role="tablist"]')
    if (tablist) break
  }
  if (!tablist) return
  const tabs = navItems(tablist, '[role="tab"]')
  if (tabs.length < 2) return
  const current = Math.max(0, tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'))
  const next = tabs[(current + (e.shiftKey ? -1 : 1) + tabs.length) % tabs.length]
  e.preventDefault()
  next.click()
}

const BIG_STEPS = 10

/** Shift with an arrow moves any slider ten steps at a time (K7). */
function handleSliderBigStep(e: KeyboardEvent) {
  const slider = e.target
  if (!e.shiftKey || !isSlider(slider as Element) || e.ctrlKey || e.altKey || e.metaKey) return
  const input = slider as HTMLInputElement
  const direction = e.key === 'ArrowRight' || e.key === 'ArrowUp' ? 1 : e.key === 'ArrowLeft' || e.key === 'ArrowDown' ? -1 : 0
  if (!direction) return
  e.preventDefault()
  const step = Number(input.step) || 1
  const min = input.min === '' ? 0 : Number(input.min)
  const max = input.max === '' ? 100 : Number(input.max)
  const next = Math.min(max, Math.max(min, Number(input.value) + direction * step * BIG_STEPS))
  if (next === Number(input.value)) return
  input.value = String(next)
  input.dispatchEvent(new Event('input', { bubbles: true }))
  input.dispatchEvent(new Event('change', { bubbles: true }))
}

/** Starts noting whether the writer last used keys or the pointer, before anything opens. */
export function installKeyboardNav() {
  watchInput()
  installMenuKeys()
  if (typeof window === 'undefined') return
  window.addEventListener('keydown', handleTabSwitch)
  window.addEventListener('keydown', handleSliderBigStep, true)
}

export const vRoving: Directive<HTMLElement, RovingOptions | NavOrientation | undefined> = {
  mounted(el, binding) {
    const options = rovingOptions(binding.value)
    ;(el as any).__roving = options
    el.setAttribute('data-nav-group', '')
    el.addEventListener('keydown', handleRovingKeydown)
    el.addEventListener('focusin', handleRovingFocusin)
    updateTabStops(el, options)
  },
  updated(el, binding) {
    const options = rovingOptions(binding.value)
    ;(el as any).__roving = options
    updateTabStops(el, options)
  },
  beforeUnmount(el) {
    el.removeEventListener('keydown', handleRovingKeydown)
    el.removeEventListener('focusin', handleRovingFocusin)
  },
}

// ── v-dialog ────────────────────────────────────────────────────────────────

export interface DialogNavOptions {
  /** The dialog's main action (Save, Export, OK): Ctrl+Enter anywhere, Enter in a one-line field. */
  primary?: string
  /** Where focus starts, when not the first field. */
  initialFocus?: string
  /** Closes the dialog on Escape, for dialogs that don't handle Escape themselves. */
  close?: () => void
}

const TABBABLE = 'button, a[href], input:not([type="hidden"]), select, textarea, [tabindex]'

function tabbables(root: HTMLElement): HTMLElement[] {
  const all = Array.from(root.querySelectorAll<HTMLElement>(TABBABLE))
    .filter((el) => !isDisabled(el) && el.tabIndex >= 0)
  // Leave out what isn't shown, when there is layout to tell.
  const shown = all.filter((el) => el.getClientRects().length > 0)
  return shown.length ? shown : all
}

/** The one-line fields Enter submits from: not text areas, not the page, not choice inputs. */
function isSingleLineField(el: Element | null): boolean {
  return el instanceof HTMLInputElement && isTextField(el)
}

interface OpenDialog {
  el: HTMLElement
  options: DialogNavOptions
  opener: HTMLElement | null
}

function primaryButton(dialog: OpenDialog): HTMLElement | null {
  const selector = dialog.options.primary
  if (!selector) return null
  const button = dialog.el.querySelector<HTMLElement>(selector)
  return button && !isDisabled(button) ? button : null
}

function handleDialogKeydown(this: HTMLElement, e: KeyboardEvent) {
  const dialog = (this as any).__dialogNav as OpenDialog | undefined
  if (!dialog || e.isComposing) return
  // A dialog inside this one (or a menu over it) handles its own keys.
  const target = e.target as HTMLElement | null
  const inner = target?.closest('[data-dialog-nav]')
  if (inner && inner !== this) return

  if (e.key === 'Tab' && !e.ctrlKey && !e.altKey && !e.metaKey) {
    const stops = tabbables(this)
    if (!stops.length) return
    const index = stops.indexOf(document.activeElement as HTMLElement)
    const atEdge = e.shiftKey ? index <= 0 : index === stops.length - 1
    if (!atEdge && index >= 0) return
    e.preventDefault()
    focusItem(e.shiftKey ? stops[stops.length - 1] : stops[0])
    return
  }

  if (e.key === 'Escape' && dialog.options.close && !e.defaultPrevented) {
    e.preventDefault()
    e.stopPropagation()
    dialog.options.close()
    return
  }

  if (e.key === 'Enter' && !e.defaultPrevented && !e.shiftKey && !e.altKey) {
    const ctrl = e.ctrlKey || e.metaKey
    // Enter in a field inside a form submits that form, as it always has.
    if (!ctrl && (!isSingleLineField(target) || target?.closest('form'))) return
    const button = primaryButton(dialog)
    if (!button) return
    e.preventDefault()
    e.stopPropagation()
    button.click()
  }
}

/** Focus at open: an element the dialog focused itself, else the chosen start, else the first field, else the first control. */
function focusDialogStart(dialog: OpenDialog) {
  const { el, options } = dialog
  if (!el.isConnected || el.contains(document.activeElement)) return
  const chosen = options.initialFocus ? el.querySelector<HTMLElement>(options.initialFocus) : null
  const stops = tabbables(el)
  const field = stops.find((stop) => isTextField(stop) || stop instanceof HTMLSelectElement)
  const start = chosen ?? field ?? stops[0]
  if (start) start.focus({ preventScroll: true })
  else {
    if (!el.hasAttribute('tabindex')) el.tabIndex = -1
    el.focus({ preventScroll: true })
  }
}

/**
 * Keyboard rules for a dialog (K8), on its dialog element: focus moves in when
 * it opens, Tab stays inside, Enter in a one-line field and Ctrl+Enter
 * anywhere press the main action, and closing gives focus back to what had it.
 */
export const vDialog: Directive<HTMLElement, DialogNavOptions | undefined> = {
  mounted(el, binding) {
    watchInput()
    const active = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const opener = closingMenu && active && closingMenu.el.contains(active) ? closingMenu.opener : active
    const dialog: OpenDialog = { el, options: binding.value ?? {}, opener }
    ;(el as any).__dialogNav = dialog
    el.setAttribute('data-dialog-nav', '')
    el.addEventListener('keydown', handleDialogKeydown)
    // After the dialog's own mount code, which may focus something itself.
    setTimeout(() => focusDialogStart(dialog), 0)
  },
  updated(el, binding) {
    const dialog = (el as any).__dialogNav as OpenDialog | undefined
    if (dialog) dialog.options = binding.value ?? {}
  },
  beforeUnmount(el) {
    const dialog = (el as any).__dialogNav as OpenDialog | undefined
    el.removeEventListener('keydown', handleDialogKeydown)
    if (!dialog) return
    queueMicrotask(() => {
      const now = document.activeElement
      if (now && now !== document.body && now.isConnected && !el.contains(now)) return
      focusOpener(dialog.opener)
    })
  },
}
